import { useCallback, useMemo, useState } from 'react';

export type OpenState = {
  [key: string]: boolean;
};

export const useOpenState = (
  initialState: OpenState
): [
  isOpen: OpenState,
  handleIsOpen: (name: string, value: boolean) => void,
  handleOpenClose: (name: string) => void,
  isAnyTrue: boolean,
  isAnyFalse: boolean,
] => {
  const [isOpen, setIsOpen] = useState<OpenState>(initialState);

  const handleIsOpen = useCallback((name: string, value: boolean) => {
    setIsOpen(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleOpenClose = useCallback((name: string) => {
    setIsOpen(prev => ({ ...prev, [name]: !prev[name] }));
  }, []);

  const hasBoolValue = useCallback((obj: OpenState, bool: boolean) => {
    for (const key in obj) {
      if (obj.hasOwnProperty(key) && obj[key] === bool) {
        return true;
      }
    }
    return false;
  }, []);

  const isAnyTrue = useMemo(
    () => hasBoolValue(isOpen, true),
    [isOpen, hasBoolValue]
  );
  const isAnyFalse = useMemo(
    () => hasBoolValue(isOpen, false),
    [isOpen, hasBoolValue]
  );

  return [isOpen, handleIsOpen, handleOpenClose, isAnyTrue, isAnyFalse];
};
